import type { GraphNode, Bend } from '../engine/types';

export interface RoutedEdge {
  sourceHandle: string;
  targetHandle: string;
  bends: Bend[];
}

const W = 160;
const H = 60;
const MARGIN = 30;

function getCenter(n: GraphNode) {
  return { x: (n.position?.x ?? 0) + W / 2, y: (n.position?.y ?? 0) + H / 2 };
}

function getSide(dx: number, dy: number): string {
  // To avoid divide by zero and handle aspect ratio: W is ~2.5x H
  // Actually, standardizing on dx, dy is enough.
  if (Math.abs(dx) > Math.abs(dy) * (W/H)) {
    return dx > 0 ? 'right' : 'left';
  } else {
    return dy > 0 ? 'bottom' : 'top';
  }
}

// Distance from point to line segment
function pointToSegmentDist(p: {x:number, y:number}, v: {x:number, y:number}, w: {x:number, y:number}) {
  const l2 = (w.x - v.x)**2 + (w.y - v.y)**2;
  if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);
  let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (v.x + t * (w.x - v.x)), p.y - (v.y + t * (w.y - v.y)));
}

export function autoRouteEdge(
  sourceNode: GraphNode,
  targetNode: GraphNode,
  allNodes: GraphNode[],
  edgeIndex: number,
  totalEdges: number
): RoutedEdge {
  const cA = getCenter(sourceNode);
  const cB = getCenter(targetNode);
  
  // Find obstacles
  let obstacle: GraphNode | null = null;
  let minD = Infinity;
  for (const n of allNodes) {
    if (n.id === sourceNode.id || n.id === targetNode.id) continue;
    const cC = getCenter(n);
    const d = pointToSegmentDist(cC, cA, cB);
    if (d < W/2 + MARGIN && d < minD) {
      // It intersects the bounding radius roughly
      // Also ensure the projection falls BETWEEN A and B
      const l2 = (cB.x - cA.x)**2 + (cB.y - cA.y)**2;
      const t = ((cC.x - cA.x) * (cB.x - cA.x) + (cC.y - cA.y) * (cB.y - cA.y)) / l2;
      if (t > 0.1 && t < 0.9) {
        minD = d;
        obstacle = n;
      }
    }
  }

  let bends: Bend[] = [];
  let nextPt = cB;
  let prevPt = cA;

  if (obstacle) {
    const cC = getCenter(obstacle);
    // Vector A -> B
    const vx = cB.x - cA.x;
    const vy = cB.y - cA.y;
    const len = Math.hypot(vx, vy);
    const nx = -vy / len;
    const ny = vx / len;
    
    // Check which side of A-B the obstacle center is
    const cross = (cC.x - cA.x) * vy - (cC.y - cA.y) * vx;
    const sign = cross > 0 ? 1 : -1;
    
    // Offset along normal
    const offset = (W/2 + MARGIN);
    
    const b1x = cC.x - (vx/len)*W*0.6 + nx * sign * offset;
    const b1y = cC.y - (vy/len)*H*0.6 + ny * sign * offset;
    
    const b2x = cC.x + (vx/len)*W*0.6 + nx * sign * offset;
    const b2y = cC.y + (vy/len)*H*0.6 + ny * sign * offset;
    
    bends.push({ x: b1x, y: b1y, mode: 'sharp' });
    bends.push({ x: b2x, y: b2y, mode: 'sharp' });
    
    nextPt = bends[0];
    prevPt = bends[bends.length - 1];
  }

  const sideA = getSide(nextPt.x - cA.x, nextPt.y - cA.y);
  const sideB = getSide(prevPt.x - cB.x, prevPt.y - cB.y);
  
  // Suffix distribution
  // 3 snaps per side: center, -1, -2
  // We distribute edgeIndex over them
  const suffixes = ['', '-1', '-2'];
  const suffix = suffixes[edgeIndex % 3];
  
  let sourceHandle = `s-${sideA}${suffix}`;
  let targetHandle = `t-${sideB}${suffix}`;
  
  // If there are more than 3 edges, we add breakpoints to visually separate them
  if (totalEdges > 3) {
    const layer = Math.floor(edgeIndex / 3);
    if (layer > 0) {
      // Add a slight deflection bend in the middle
      const mx = (cA.x + cB.x) / 2;
      const my = (cA.y + cB.y) / 2;
      const vx = cB.x - cA.x;
      const vy = cB.y - cA.y;
      const len = Math.hypot(vx, vy) || 1;
      const nx = -vy / len;
      const ny = vx / len;
      
      const deflection = layer * 30 * (edgeIndex % 2 === 0 ? 1 : -1);
      
      // Insert in the middle of bends array
      bends.splice(bends.length / 2, 0, {
        x: mx + nx * deflection,
        y: my + ny * deflection,
        mode: 'sharp'
      });
    }
  }

  return { sourceHandle, targetHandle, bends };
}
